import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import Typography from 'LuxKit/Typography/Typography'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import ModalContext from 'contexts/ModalContext'

interface Props {
  totalPrice: number
  totalItemCount: number
  hasCheckoutReadyItems: boolean
  showReadyItemsOnlyNote: boolean
  isLoggedIn: boolean
  onCheckout: () => Promise<void>
}

function CartPanelFooter({
  totalPrice,
  totalItemCount,
  hasCheckoutReadyItems,
  showReadyItemsOnlyNote,
  isLoggedIn,
  onCheckout,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  const totalHeading = intl.formatMessage(
    {
      id: 'wrcligwUpGIpkDJ9iUs3',
      defaultMessage: 'Total ({count, plural, one {# item} other {# items}})',
      description: 'Cart panel footer heading showing total with item count',
    },
    { count: totalItemCount },
  )

  const checkoutLabel = !isLoggedIn
    ? intl.formatMessage({
        id: 'PsWbPJA5WZimhO6Dv0Zl',
        defaultMessage: 'Log in to checkout',
        description: 'Cart panel checkout button text when user is not logged in',
      })
    : intl.formatMessage({
        id: '5tiwR3KtXkphPkrPc0tW',
        defaultMessage: 'Checkout now',
        description: 'Primary checkout button text shown in the trip planner cart panel footer',
      })

  const handleCheckoutClick = useCallback(() => {
    if (!isLoggedIn) {
      showModal(<AccountAccessModal />)
      return
    }
    onCheckout()
  }, [isLoggedIn, showModal, onCheckout])

  return (
    <Sheet padding={16} background="normal-background">
      <Group direction="vertical" gap={12}>
        <Group direction="horizontal" horizontalAlign="space-between">
          <Group direction="vertical">
            <Heading variant="heading5">{totalHeading}</Heading>
            <PriceRowTaxesAndFees />
            {showReadyItemsOnlyNote && <BodyText variant="small" colour="neutral-three">
              {intl.formatMessage({
                id: 'hv52vMT3ulBalYfuWSIX',
                defaultMessage: 'Checkout includes ready items only',
                description: 'Cart panel footer note explaining checkout skips items that need user attention',
              })}
            </BodyText>}
          </Group>
          <Typography align="end">
            <PriceRowPrice
              price={totalPrice}
              priceFormat="preciseDollar"
              saleUnit=""
              size="M"
            />
          </Typography>
        </Group>
        <TextButton
          kind="primary"
          size="large"
          disabled={!hasCheckoutReadyItems && isLoggedIn}
          onClick={handleCheckoutClick}
          fit="full-width"
        >
          {checkoutLabel}
        </TextButton>
      </Group>
    </Sheet>
  )
}

export default CartPanelFooter
